<script setup lang="ts">
    import { FilterStoreName, isCheckboxFilterType } from '@/js/services/api/filter/types';
    import { __ } from '@/js/stores/expressions';
    import { useFilterStoreByName } from '@/js/stores/filter';
    import { computed, ref } from 'vue';
    import ActiveFilters from '../filter/ActiveFilters.vue';
    import { isNullOrUndefined } from '@/js/utils/isNullOrUndefined';
    import { showTrackerGroupTypeToggle } from './types';

    const props = withDefaults(
        defineProps<{
            filterStoreName: FilterStoreName;
            readonly?: boolean;
            allFiltersVisible?: boolean | null;
            matchTablePadding?: boolean;
        }>(),
        { allFiltersVisible: null, matchTablePadding: false }
    );

    const filter = useFilterStoreByName(props.filterStoreName);

    const expanded = ref(false);

    const hideDateFilter = props.filterStoreName === FilterStoreName.ScheduleCoachingFilter;

    const activeFiltersCount = computed(() => {
        // Default to 1 for date range filter if no other filters are selected
        if (!filter.selected || !filter.all) {
            return hideDateFilter ? 0 : 1;
        }

        return (
            (hideDateFilter ? 0 : 1) + // For date range
            (filter.selected.skillFilter?.length || 0) +
            (filter.selected.queueFilter?.length || 0) +
            (filter.selected.levelFilter?.length || 0) +
            (filter.selected.trackerFilter?.filter?.length || 0) +
            (showTrackerGroupTypeToggle &&
            filter.selected.trackerFilter?.selectedGroupType !== 'All'
                ? 1
                : 0) +
            (filter.selected.channelFilter?.length || 0) +
            (filter.selected.categoryFilter?.length || 0) +
            (filter.selected.sentimentAgentFilter?.length || 0) +
            (filter.selected.sentimentAgentDirectionFilter?.length || 0) +
            (filter.selected.sentimentCustomerFilter?.length || 0) +
            (filter.selected.sentimentCustomerDirectionFilter?.length || 0) +
            (filter.all?.recordingStart ? 1 : 0) +
            (filter.all?.recordingEnd ? 1 : 0) +
            (filter.selected.keywordFilter?.words?.length || 0) +
            (filter.selected.keywordFilter?.excludedWords?.length || 0) +
            (filter.selected.protocolFormDefinitionFilter?.length || 0) +
            (filter.selected.submittedByUserFilter?.length || 0) +
            (filter.selected.protocolAgentFilter?.length || 0) +
            (filter.selected.protocolGradeFilter?.length || 0) +
            (isNullOrUndefined(filter.selected.submittedByMe) ? 0 : 1) +
            (isNullOrUndefined(filter.selected.createdByAi) && !filter.selected.editedByHuman
                ? 0
                : 1) +
            (isNullOrUndefined(filter.selected.pendingApproval) ? 0 : 1) +
            (isNullOrUndefined(filter.selected.actionRequired) ? 0 : 1) +
            (filter.selected.customDataFilter?.reduce(function (count, custom) {
                if (isCheckboxFilterType(custom.filterType)) {
                    return count + (custom.filter?.length || 0);
                } else if (custom.filterType === 'Text') {
                    return count + (custom.words?.length || 0);
                }

                return count;
            }, 0) || 0) +
            (filter.selected.onlyResolved === true || filter.selected.onlyResolved === false
                ? 1
                : 0) +
            (filter.selected.hasTranscriptions === false ? 1 : 0)
        );
    });
</script>
<template>
    <div class="relative z-20 flex-grow w-0">
        <slot />
        <aside class="overflow-y-auto max-h-screen">
            <h2
                class="text-xs font-medium inline-flex"
                :class="props.matchTablePadding ? 'px-7.5' : 'px-5'"
            >
                {{ __('active-filters') }}
                ({{ activeFiltersCount }})
            </h2>
            <div
                class="flex flex-wrap items-center gap-2 w-full bg-white pt-2 pb-4 pr-4 sm:pr-12"
                :class="props.matchTablePadding ? 'pl-7.5' : 'pl-5'"
            >
                <ActiveFilters
                    v-model:expanded="expanded"
                    :filter-store-name="props.filterStoreName"
                    :show-clear="activeFiltersCount > 1"
                    :readonly="props.readonly"
                    @reset-filters="expanded = false"
                >
                    <template v-if="$slots['save-filter']" #save-filter>
                        <slot name="save-filter" />
                    </template>
                    <template v-if="$slots['favorite-filters']" #favorite-filters>
                        <slot name="favorite-filters" />
                    </template>
                </ActiveFilters>
            </div>
        </aside>
    </div>
    <div
        v-if="props.allFiltersVisible !== null"
        aria-hidden="true"
        class="shrink-0"
        :class="props.allFiltersVisible ? 'w-0' : 'w-36'"
    />
</template>
